import React, { useEffect, useRef, useState } from 'react';

// Homepage hero film (media/homepage-film — storyboard, shots, render
// pipeline). Same contract as the Fast Legal hero: a full-bleed stage
// behind the hero copy (100vw, escapes the column), the poster paints first,
// the clip attaches after first paint so it never competes for LCP, reduced
// motion and Save-Data stay on the poster, narrow viewports get the 720p
// cut, and a failed autoplay falls back to the poster. A bottom-weighted
// scrim keeps the copy legible over the brightest frames.
//
// Dormant until REACT_APP_HERO_FILM_VERSION is set at build time: with no
// version there is no film yet, and `fallback` (the 3D scene / SVG chassis)
// renders instead. Bump the version on every re-cut — /media/** is cached
// for a week and the file names never change, so the query string is what
// busts the cache.
const HERO_VIDEO_BASE = (process.env.REACT_APP_HERO_VIDEO_BASE || '/media/homepage-film').replace(/\/$/, '');
export const HERO_FILM_VERSION = process.env.REACT_APP_HERO_FILM_VERSION || '';
export const heroFilmEnabled = () => Boolean(HERO_FILM_VERSION);
const film = (name) => `${HERO_VIDEO_BASE}/${name}?v=${HERO_FILM_VERSION}`;

export default function HeroFilm({ fallback = null, alt = '' }) {
  const [cut, setCut] = useState(null); // null | 'poster' | 'desktop' | 'mobile'
  const [ready, setReady] = useState(false);
  // The loop is a muted backdrop. Sound lives in the invoice film
  // (components/FilmLightbox.js), opened from the hero copy.
  const videoRef = useRef(null);
  const enabled = heroFilmEnabled();

  useEffect(() => {
    if (!enabled) return undefined;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const saveData = Boolean(navigator.connection && navigator.connection.saveData);
    if (reduce || saveData) { setCut('poster'); return undefined; }
    const pick = () => setCut(window.matchMedia('(max-width: 719px)').matches ? 'mobile' : 'desktop');
    if (window.requestIdleCallback) {
      const id = window.requestIdleCallback(pick, { timeout: 1500 });
      return () => window.cancelIdleCallback(id);
    }
    const id = window.setTimeout(pick, 250);
    return () => window.clearTimeout(id);
  }, [enabled]);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    el.muted = true; // React sets the property, not the attribute; be explicit for autoplay policy
    const play = el.play();
    if (play && play.catch) play.catch(() => setCut('poster'));
  }, [cut]);

  if (!enabled) return fallback;

  return (
    <>
      <div className={`cs-hero-film ${ready ? 'is-ready' : ''}`} aria-hidden="true">
        <img className="cs-hero-film-poster" src={film('hero-poster.jpg')} alt={alt} decoding="async" fetchpriority="high" />
        {(cut === 'desktop' || cut === 'mobile') && (
          <video
            key={cut}
            ref={videoRef}
            className="cs-hero-film-video"
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            onPlaying={() => setReady(true)}
            onError={() => setCut('poster')}
          >
            {cut === 'desktop' && <source src={film('hero-1080.webm')} type="video/webm" />}
            {cut === 'desktop' && <source src={film('hero-1080.mp4')} type="video/mp4" />}
            {cut === 'mobile' && <source src={film('hero-720-mobile.mp4')} type="video/mp4" />}
          </video>
        )}
        <div className="cs-hero-film-scrim" />
      </div>
    </>
  );
}
